import React, { FC, useContext } from 'react'
import { DragDropContext, DropResult } from 'react-beautiful-dnd'
import { IChannel } from 'shared/types/channel'
import { CollectionContext } from '..'
import { moveChannel } from 'shared/api/channels'
import MessageContext from 'shared/components/context/message'
import RefreshDataContext from 'shared/components/context/refresh-data'

const reorder = (channels: IChannel[], startIndex: number, endIndex: number) => {
    channels = [...channels]
    const [removed] = channels.splice(startIndex, 1)
    channels.splice(endIndex, 0, removed)
    return channels
}

const insert = (channels: IChannel[], indexAt: number, items: IChannel[]) => {
    const result = [...channels]
    result.splice(indexAt, 0, ...items)
    return result
}

type Props = { setReordering: (isReordering: boolean) => void }

const CollectionDnD: FC<Props> = ({ children, setReordering }) => {
    const { channels, getChannelChildren } = useContext(CollectionContext)
    const { sendMessage } = useContext(MessageContext)
    const { refreshChannel } = useContext(RefreshDataContext)
    const onDragEnd = async (result: DropResult) => {
        const { source, destination } = result
        // dropped outside the list
        if (!destination) {
            return
        }
        const { droppableId: sourceChannelId } = source
        const { droppableId: targetId } = destination
        const isReparenting = sourceChannelId !== targetId
        if (source.index !== destination.index || isReparenting) {
            const destinationChannel = channels[parseInt(targetId)]
            const parentChannel = channels[parseInt(sourceChannelId)]
            setReordering(true)
            let parentChildren = parentChannel.children || (await getChannelChildren(parentChannel))
            let destinationChildren = destinationChannel.children || (await getChannelChildren(destinationChannel))
            const channelToMove = parentChildren[source.index]
            const modifiedChildren = isReparenting
                ? insert(destinationChildren, destination.index, [channelToMove])
                : reorder(parentChildren, source.index, destination.index)
            if (isReparenting) {
                const oldParentChildren = parentChildren.filter(c => c.id !== channelToMove.id)
                refreshChannel(
                    { ...parentChannel, children: oldParentChildren, has_children: oldParentChildren.length > 0 },
                    { isReordering: true }
                )
            }
            refreshChannel(
                { ...destinationChannel, children: modifiedChildren, has_children: modifiedChildren.length > 0 },
                { isReordering: true }
            )
            try {
                await moveChannel(
                    channelToMove.id,
                    isReparenting ? destinationChannel.id : destinationChildren[destination.index].id,
                    isReparenting ? 'first-child' : source.index < destination.index ? 'right' : 'left'
                )
                if (isReparenting && destinationChildren.length > 0) {
                    const destinationIndex =
                        destinationChildren[destination.index]?.id ||
                        // dropping at the end of the list will have too high an index,
                        // assuming that's what's happening here and dropping the target on the end
                        destinationChildren[destinationChildren.length - 1].id
                    await moveChannel(
                        channelToMove.id,
                        destinationIndex,
                        source.index < destination.index ? 'right' : 'left'
                    )
                }
            } catch (error) {
                console.error(`Move channel error:`, error)
                refreshChannel({ ...destinationChannel })
                if (isReparenting) {
                    refreshChannel({ ...parentChannel })
                }
                // TODO error for reparent
                sendMessage(`There was an error moving the channel :(`, 'error')
            }
            setReordering(false)
        }
    }
    return <DragDropContext onDragEnd={onDragEnd}>{children}</DragDropContext>
}
export default CollectionDnD
